import { Fragment, Key } from 'react';
import useComment from '../../../hooks/useCommentHook';
import { useCommentsStore } from '../../../stores/commentsStore';
import { Button } from '../../atoms/Button';
import NamePill from '../../molecules/NamePill';
import { Typography } from '../../molecules/Typography';
import CommentCard from './components/comment/indext';
import InputDiv from './components/inputDiv';
import BrainLoadingWrapper from '../../molecules/BrainLoading';

export interface ICommentAPI {
  area: string;
  createdAt: Date;
  display: boolean;
  id: string;
  isAdmin: boolean;
  isFixed: boolean;
  isLiked: boolean;
  level: string;
  likes: any[];
  children: any[];
  qtdLikes: number;
  subarea: string;
  targetId: any;
  targetType: string;
  text: string;
  updatedAt: Date;
  user: any;
}

interface IComment {
  title: string;
  targetId: string;
  parent?: string;
  targetType: 'Lesson' | 'Exercise' | 'Live';
  isModal?: boolean;
  showTitle?: boolean;
  titleSize?: 'h3' | 'h5';
}

export default function Comment({
  title = 'Comentários',
  targetId,
  targetType,
  isModal = false,
  showTitle = true,
  titleSize = 'h3',
}: IComment) {
  const { isLoading, addComment, likeComment } = useComment(targetId);
  const {
    comments,
    totalComments,
    totalShowAll,
    enableShowAll,
    showAllComments,
    addCommentState,
    upvoteCommentState,
  } = useCommentsStore();

  const upvoteComment = async (id: string) => {
    const data = await likeComment(id);
    upvoteCommentState(data);
  };

  const newComment = async (type: string, text: string, id?: string) => {
    if (text.length < 3) {
      return;
    }
    const body = {
      text,
      targetType: targetType,
      targetId: targetId,
      parent: id,
    };
    if (!id) {
      delete body.parent;
    }
    const data = await addComment(body);
    addCommentState(data);
  };

  return (
    <div className='m-auto flex w-full flex-col gap-xxl'>
      {showTitle && (
        <div className='flex w-full flex-row items-end justify-between'>
          <Typography type='title' size={titleSize}>
            {title}
          </Typography>
          <Typography size='small'>{totalComments} comentários</Typography>
        </div>
      )}
      <InputDiv onSubmit={(text: string) => newComment('comment', text)} />
      {isLoading ? <BrainLoadingWrapper /> : (
        <Fragment>
          <div className='flex h-fit w-full flex-col gap-xl'>
            {comments.map((it: ICommentAPI, i: Key | null | undefined) => (
              <div className='items-top flex flex-row gap-[24px]' key={i}>
                <div className='xs:hidden md:block md:w-12'>
                  <NamePill
                    profile={it.user.picture}
                    name={it.user.name}
                    color={isModal ? 'mono' : 'primary'}
                  />
                </div>
                <div className='flex w-5/6 flex-1 flex-col gap-xl'>
                  <CommentCard
                    comment={it}
                    onSubmit={(text: string) =>
                      newComment('reply', text, it.id)
                    }
                    onLike={() => upvoteComment(it.id)}
                    key={i}
                    isModal={isModal}
                  />
                  {it.children &&
                    it.children.length > 0 &&
                    it.children.map((reply: ICommentAPI, i: number) => (
                      <div
                        className='items-top flex flex-row gap-[24px]'
                        key={i}
                      >
                        <div className='xs:hidden md:block md:w-12'>
                          <NamePill
                            profile={reply.user.picture}
                            name={reply.user.name}
                            color={isModal ? 'mono' : 'primary'}
                          />
                        </div>
                        <div className='flex flex-1'>
                          <CommentCard
                            comment={reply}
                            disableReply={true}
                            onSubmit={() => {}}
                            key={i}
                            onLike={() => {
                              upvoteComment(reply.id);
                            }}
                            isModal={isModal}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
          <div className='mb-10 flex h-fit w-full justify-end'>
            {enableShowAll && (
              <Button
                variant='text'
                size='regular'
                LeftIcon='expand_more'
                className='p-0'
                onClick={() => showAllComments()}
              >
                Ver mais {totalShowAll} comentários
              </Button>
            )}
          </div>
        </Fragment>
      )}
    </div>
  );
}
